/**
 * Live dashboard metric snapshots, tracked per activity window so each
 * watched window is rejoined after a reconnect and a 7-day viewer never
 * receives a 30-day snapshot (the server fans out to per-window rooms).
 */

import type { DashboardMetricsResponse } from '@/types/activity'
import { WebSocketClient } from '../client'
import { SubscriptionRegistry } from '../subscriptions'

export class DashboardLiveApi {
  private readonly metrics: SubscriptionRegistry<number, DashboardMetricsResponse>

  constructor(private readonly client: WebSocketClient) {
    this.metrics = new SubscriptionRegistry({
      join: (days) => this.client.emitWhenConnected('join_dashboard_metrics', { days }),
      leave: (days) => this.client.emitIfSocket('leave_dashboard_metrics', { days }),
    })
  }

  /**
   * Subscribe to metric snapshots for one activity window. Joins the
   * per-window room when its subscriber set goes empty → non-empty and
   * leaves it when the last subscriber for that window unsubscribes.
   */
  subscribeToDashboardMetrics(
    days: number,
    callback: (data: DashboardMetricsResponse) => void
  ): () => void {
    if (!this.client.hasSocket()) {
      console.warn('WebSocket not connected. Call connect() first.')
      return () => {}
    }

    this.client.attachOnce('dashboard_metrics', (event) => {
      const snapshot = event as { days: number; data: DashboardMetricsResponse }
      this.metrics.dispatch(snapshot.days, snapshot.data)
    })

    return this.metrics.subscribe(days, callback)
  }

  /** Rejoin every watched window's room after a reconnect. */
  replay(): void {
    this.metrics.replay()
  }

  clear(): void {
    this.metrics.clear()
  }
}
